// ============================================================================
// Role Configuration - Single Source of Truth
// EduPath Africa v2.0 - Routing Architecture
// ============================================================================

/**
 * All user roles in the system.
 * IMPORTANT: Always use UPPERCASE for role values internally.
 */
export type UserRole = 
  | 'LEARNER' 
  | 'GUARDIAN' 
  | 'TEACHER' 
  | 'COUNSELOR' 
  | 'SCHOOL_ADMIN' 
  | 'ADMIN' 
  | 'SUPER_ADMIN';

/**
 * Normalize role to uppercase.
 * Handles backend returning lowercase roles like "learner".
 */
export function normalizeRole(role: string | undefined | null): UserRole {
  if (!role) return 'LEARNER'; // Default fallback
  const normalized = role.toUpperCase() as UserRole;
  
  // Validate it's a known role
  const validRoles: UserRole[] = [
    'LEARNER', 'GUARDIAN', 'TEACHER', 'COUNSELOR', 
    'SCHOOL_ADMIN', 'ADMIN', 'SUPER_ADMIN'
  ];
  
  return validRoles.includes(normalized) ? normalized : 'LEARNER';
}

/**
 * Role-to-dashboard mapping.
 * Each role has exactly one default dashboard.
 */
export const ROLE_DASHBOARDS: Record<UserRole, string> = {
  LEARNER: '/student/dashboard',
  GUARDIAN: '/guardian/dashboard',
  TEACHER: '/teacher/dashboard',
  COUNSELOR: '/counselor/dashboard',
  SCHOOL_ADMIN: '/school/dashboard',
  ADMIN: '/admin/dashboard',
  SUPER_ADMIN: '/admin/dashboard',
};

/**
 * Get the dashboard path for a given role.
 */
export function getDashboardForRole(role: UserRole | string | null | undefined): string {
  const normalizedRole = normalizeRole(role as string);
  return ROLE_DASHBOARDS[normalizedRole] || '/student/dashboard';
}

/**
 * Role-to-allowed-routes mapping.
 * Defines which route prefixes each role can access.
 */
export const ROLE_ROUTE_ACCESS: Record<UserRole, string[]> = {
  LEARNER: ['/student', '/assessments', '/reports', '/upgrade', '/profile'],
  GUARDIAN: ['/guardian', '/children', '/aris', '/profile'],
  TEACHER: ['/teacher', '/students', '/ratings', '/profile'],
  COUNSELOR: ['/counselor', '/teacher', '/escalations', '/students', '/profile'],
  SCHOOL_ADMIN: ['/school', '/enrollments', '/school-reports', '/profile'],
  ADMIN: ['/admin', '/schools', '/licenses', '/audit', '/users', '/profile'],
  SUPER_ADMIN: ['/admin', '/schools', '/licenses', '/audit', '/users', '/system', '/profile'],
};

/**
 * Check if a role can access a given path.
 */
export function canRoleAccessPath(role: UserRole | string, path: string): boolean {
  const normalizedRole = normalizeRole(role as string);
  const allowedPrefixes = ROLE_ROUTE_ACCESS[normalizedRole] || [];
  
  // Always allow root and auth routes
  if (path === '/' || path.startsWith('/login') || path.startsWith('/register') || 
      path.startsWith('/forgot-password') || path.startsWith('/verify')) {
    return true;
  }
  
  return allowedPrefixes.some(prefix => path.startsWith(prefix));
}

/**
 * Route groups for easier configuration in App.tsx.
 */
export const ROUTE_GROUPS = {
  AUTH: ['/login', '/register', '/forgot-password', '/reset-password', '/verify'],
  STUDENT: ['/student', '/assessments', '/reports'],
  GUARDIAN: ['/guardian', '/children', '/aris'],
  TEACHER: ['/teacher', '/students'],
  COUNSELOR: ['/counselor', '/escalations'],
  SCHOOL_ADMIN: ['/school', '/enrollments'],
  ADMIN: ['/admin', '/schools', '/licenses', '/audit', '/users'],
};

/**
 * Tier-based feature access.
 */
export type TierType = 'basic' | 'premium' | 'guardian';

export const TIER_FEATURES: Record<TierType, string[]> = {
  basic: ['assessment', 'basic-report', 'profile'],
  premium: ['assessment', 'basic-report', 'premium-report', 'school-matching', 'pdf-download', 'profile'],
  guardian: ['assessment', 'basic-report', 'premium-report', 'school-matching', 'pdf-download', 
             'aris-chat', 'growth-tracking', 'multi-child', 'profile'],
};

export function canTierAccessFeature(tier: TierType, feature: string): boolean {
  return TIER_FEATURES[tier]?.includes(feature) ?? false;
}
